import React from 'react';
import {
    ChevronUp,
    ChevronDown,
    ChevronsLeft,
    ChevronsRight,
    ChevronLeft,
    ChevronRight,
} from 'lucide-react';
import { useSmoothLoading } from 'lib/use-smooth-loading';
import { Skeleton } from 'components/ui/skeleton';
import { UARAgeingRow } from '../api';

interface Props {
    data: UARAgeingRow[];
    loading: boolean;
    page: number;
    pageSize: number;
    totalItems: number;
    sortBy: keyof UARAgeingRow;
    sortOrder: 'asc' | 'desc';
    onSortChange: (column: keyof UARAgeingRow) => void;
    onPageChange: (page: number) => void;
    onPageSizeChange: (size: number) => void;
    hasLoadedOnce: boolean;
    hasFiltersSelected: boolean;
    error?: string;
    conversionType: 'INR' | 'Lakhs' | 'Crores';
    onCustomerClick: (customerCode: string, customerName: string) => void;
}

const columns: Array<{
    key: keyof UARAgeingRow | 'collection_responsibility';
    label: string;
    isNumeric: boolean;
}> = [
        { key: 'customer_name', label: 'Customer', isNumeric: false },
        { key: 'collection_responsibility', label: 'Collection Responsibility', isNumeric: false },
        { key: 'uar', label: 'UAR', isNumeric: true },
        { key: 'mr_count', label: 'MR Count', isNumeric: true },
        { key: 'bucket_1_30', label: '1–30', isNumeric: true },
        { key: 'bucket_31_60', label: '31–60', isNumeric: true },
        { key: 'bucket_61_90', label: '61–90', isNumeric: true },
        { key: 'bucket_91_120', label: '91–120', isNumeric: true },
        { key: 'bucket_121_180', label: '121–180', isNumeric: true },
        { key: 'bucket_over_180', label: '>180', isNumeric: true },
    ];

const UarTable: React.FC<Props> = ({
    data,
    loading,
    page,
    pageSize,
    totalItems,
    sortBy,
    sortOrder,
    onSortChange,
    onPageChange,
    onPageSizeChange,
    hasLoadedOnce,
    hasFiltersSelected,
    error,
    conversionType,
    onCustomerClick,
}) => {
    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
    const { isLoading, showContent } = useSmoothLoading(loading, {
        minLoadingTime: 150,
        debounceTime: 150,
    });

    const formatNumber = (value: number): string => {
        let displayValue = value;

        if (conversionType === 'Lakhs') {
            displayValue = value / 100000;
        }
        if (conversionType === 'Crores') {
            displayValue = value / 10000000;
        }

        return displayValue.toLocaleString('en-IN', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
    };

    const renderLoadingRows = () =>
        Array.from({ length: 10 }).map((_, i) => (
            <tr key={i}>
                {columns.map((_, j) => (
                    <td key={j} className="border px-2 py-1.5">
                        <Skeleton className="h-4 w-full" />
                    </td>
                ))}
            </tr>
        ));

    const opacityClass = isLoading || showContent ? 'opacity-100' : 'opacity-0';

    return (
        <div className={`flex flex-col h-full min-h-0 transition-opacity duration-300 ${opacityClass}`}>
            <div className="flex-1 overflow-auto border border-gray-200">
                <table className="w-full border-collapse text-sm">
                    <thead>
                        <tr className="bg-gray-100">
                            {columns.map((col) => {
                                const isActive = sortBy === col.key;
                                return (
                                    <th
                                        key={col.label}
                                        onClick={() =>
                                            col.key !== 'collection_responsibility' &&
                                            onSortChange(col.key as keyof UARAgeingRow)
                                        }
                                        className={`border px-2 py-2 font-bold cursor-pointer ${
                                            col.isNumeric ? 'text-right' : 'text-left'
                                        }`}
                                    >
                                        <div className="flex items-center gap-1">
                                            {col.label}
                                            {isActive && (
                                                sortOrder === 'asc'
                                                    ? <ChevronUp size={14} />
                                                    : <ChevronDown size={14} />
                                            )}
                                        </div>
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>

                    <tbody>
                        {isLoading || (hasFiltersSelected && !hasLoadedOnce) ? (
                            renderLoadingRows()
                        ) : !hasFiltersSelected ? (
                            <tr>
                                <td colSpan={columns.length} className="py-20 text-center text-gray-500">
                                    Please select filters to fetch data.
                                </td>
                            </tr>
                        ) : error ? (
                            <tr>
                                <td colSpan={columns.length} className="py-20 text-center text-red-600">
                                    {error}
                                </td>
                            </tr>
                        ) : data.length === 0 ? (
                            <tr>
                                <td colSpan={columns.length} className="py-20 text-center text-gray-500">
                                    No data available.
                                </td>
                            </tr>
                        ) : (
                            data.map((row, idx) => (
                                <tr key={idx} className={`cursor-pointer hover:bg-gray-100 ${idx === 0 ? 'bg-blue-50 font-semibold' : ''}`}
                                    onClick={() => row.customer_code !== 'TOTAL' && onCustomerClick(row.customer_code, row.customer_name)}>
                                    {columns.map((col) => (
                                        <td key={col.label} className={`border px-2 py-1.5 ${col.isNumeric ? 'text-right' : 'text-left'}`}>
                                            {col.key === 'collection_responsibility'
                                                ? row.collection_responsibility.length > 0
                                                    ? row.collection_responsibility.join(', ')
                                                    : '-'
                                                : col.key === 'customer_name'
                                                    ? row.customer_name
                                                    : col.key === 'mr_count'
                                                        ? row.mr_count.toLocaleString('en-IN')
                                                        : col.isNumeric
                                                            ? formatNumber(row[col.key as keyof UARAgeingRow] as number)
                                                            : row[col.key as keyof UARAgeingRow]
                                            }
                                        </td>
                                    ))}
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {hasFiltersSelected && !error && (
                <div className="flex items-center justify-between shrink-0 pt-4">
                    <span className="text-sm text-gray-600">
                        Showing{' '}
                        <span className="font-semibold">{totalItems === 0 ? 0 : (page - 1) * pageSize + 1}</span>{' '}
                        to{' '}
                        <span className="font-semibold">{Math.min(page * pageSize, totalItems)}</span> of{' '}
                        <span className="font-semibold">{totalItems}</span> results
                    </span>

                    <div className="flex items-center gap-3 text-sm space-x-10">
                        <div className="flex items-center gap-1">
                            <span className="text-gray-600 mr-1">Rows per page</span>
                            <select
                                value={pageSize}
                                onChange={(e) => onPageSizeChange(Number(e.target.value))}
                                className="border border-gray-300 rounded px-2 py-1"
                            >
                                {[10, 20, 50, 100].map((s) => (
                                    <option key={s} value={s}>
                                        {s}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <span className="text-gray-600">
                            Page <strong>{page}</strong> of <strong>{totalPages}</strong>
                        </span>

                        <div className="flex items-center gap-1">
                            <button
                                disabled={page === 1}
                                onClick={() => onPageChange(1)}
                                className="p-1 border border-gray-300 rounded disabled:opacity-50 hover:bg-gray-50"
                            >
                                <ChevronsLeft size={16} />
                            </button>

                            <button
                                disabled={page === 1}
                                onClick={() => onPageChange(page - 1)}
                                className="p-1 border border-gray-300 rounded disabled:opacity-50 hover:bg-gray-50"
                            >
                                <ChevronLeft size={16} />
                            </button>

                            <button
                                disabled={page === totalPages}
                                onClick={() => onPageChange(page + 1)}
                                className="p-1 border border-gray-300 rounded disabled:opacity-50 hover:bg-gray-50"
                            >
                                <ChevronRight size={16} />
                            </button>

                            <button
                                disabled={page === totalPages}
                                onClick={() => onPageChange(totalPages)}
                                className="p-1 border border-gray-300 rounded disabled:opacity-50 hover:bg-gray-50"
                            >
                                <ChevronsRight size={16} />
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UarTable;
